import { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Input,
  InputGroup,
  InputLeftAddon,
  Textarea,
  Box,
  Text,
  Divider,
  Flex,
  Icon,
  Spinner,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAttachMoney,
  MdChevronRight,
  MdSkipNext,
} from 'react-icons/md';
import Steps from 'components/ui/Steps';
import type { Step as StepItem } from 'components/ui/Steps';
import { useServiceItems } from 'hooks/useServiceItems';
import { useRegisterSessionCharge, useCompleteSession } from 'hooks/useSessions';
import {
  EstadoServicio,
  RegisterSessionChargeDto,
  Session,
  SoapOption,
  CompleteSessionDto,
} from 'types/models';

type DiscountMode = 'amount' | 'percent';
type Step = 1 | 2;

const MODAL_STEPS: StepItem[] = [
  { id: 'charge', title: 'Cobro',      description: 'Servicio y descuento' },
  { id: 'soap',   title: 'Nota SOAP',  description: 'Opcional'             },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  patientId: string;
  episodeId: string;
  planId: string;
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function RegisterChargeModal({
  isOpen, onClose, session, patientId, episodeId, planId,
}: Props) {
  const { data: serviceItems, isLoading: loadingItems } = useServiceItems();
  const { mutate: registerCharge, isPending: isPendingCharge } = useRegisterSessionCharge();
  const { mutate: completeSession, isPending: isPendingComplete } = useCompleteSession();
  const isPending = isPendingCharge || isPendingComplete;

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(1);

  // ── Step 1: charge ────────────────────────────────────────────────────────
  const [serviceItemId,  setServiceItemId]  = useState('');
  const [discountMode,   setDiscountMode]   = useState<DiscountMode>('amount');
  const [discountValue,  setDiscountValue]  = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [step1Errors,    setStep1Errors]    = useState<Record<string, string>>({});

  // ── Step 2: SOAP ──────────────────────────────────────────────────────────
  const [motivoSesion,       setMotivoSesion]       = useState('');
  const [evaDolor,           setEvaDolor]           = useState('');
  const [sintomasReferidos,  setSintomasReferidos]  = useState('');
  const [hallazgos,          setHallazgos]          = useState('');
  const [tecnicas,           setTecnicas]           = useState('');
  const [objetivosProx,      setObjetivosProx]      = useState('');
  const [motivoError,        setMotivoError]        = useState(false);

  // ── Derived: charge ───────────────────────────────────────────────────────
  const activeItems = useMemo(
    () => serviceItems?.filter(s => s.status === EstadoServicio.ACTIVO) ?? [],
    [serviceItems],
  );
  const selectedItem = useMemo(
    () => activeItems.find(s => s.id === serviceItemId) ?? null,
    [activeItems, serviceItemId],
  );
  const basePrice   = Number(selectedItem?.basePrice ?? 0);
  const discountNum = discountValue !== '' ? parseFloat(discountValue) : 0;
  const discountAmt = discountMode === 'amount'
    ? discountNum
    : (basePrice * discountNum) / 100;
  const totalAmount = Math.max(0, basePrice - discountAmt);
  const hasDiscount = discountAmt > 0 && !isNaN(discountAmt);

  // ── Validation step 1 ─────────────────────────────────────────────────────
  const validateStep1 = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!serviceItemId) errs.serviceItemId = 'Seleccione un servicio del catálogo';
    if (discountValue !== '') {
      if (discountNum < 0) {
        errs.discountValue = 'El descuento no puede ser negativo';
      } else if (discountMode === 'percent' && discountNum > 100) {
        errs.discountValue = 'El porcentaje no puede exceder 100%';
      } else if (discountMode === 'amount' && discountNum > basePrice) {
        errs.discountValue = 'El descuento supera el precio base';
      }
    }
    if (hasDiscount && !discountReason.trim()) {
      errs.discountReason = 'El motivo es obligatorio cuando se aplica descuento';
    }
    return errs;
  };

  const handleContinue = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length) { setStep1Errors(errs); return; }
    setStep1Errors({});
    setStep(2);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const buildChargePayload = (soapOption: SoapOption): RegisterSessionChargeDto => ({
    serviceItemId,
    soapOption,
    ...(hasDiscount && discountMode === 'amount'  ? { discountAmount: discountNum }  : {}),
    ...(hasDiscount && discountMode === 'percent' ? { discountPercent: discountNum } : {}),
    ...(hasDiscount                               ? { discountReason: discountReason.trim() } : {}),
  });

  const handleSkipSoap = () => {
    registerCharge(
      { patientId, episodeId, planId, sessionId: session.id, payload: buildChargePayload('DEFER_SOAP') },
      { onSuccess: onClose },
    );
  };

  const handleSaveWithSoap = () => {
    if (motivoSesion.trim().length < 5) { setMotivoError(true); return; }
    setMotivoError(false);

    const chargePayload = buildChargePayload('COMPLETE_NOW');

    registerCharge(
      { patientId, episodeId, planId, sessionId: session.id, payload: chargePayload },
      {
        onSuccess: () => {
          const soapPayload: CompleteSessionDto = {
            subjetivo: {
              motivoSesion: motivoSesion.trim(),
              evaDolor: evaDolor !== '' ? Number(evaDolor) : undefined,
              sintomasReferidos: sintomasReferidos.trim() || undefined,
            },
            objetivo: {
              hallazgosExamenFisico: hallazgos.trim() || undefined,
            },
            plan: tecnicas || objetivosProx
              ? {
                  tecnicasAplicadas: tecnicas.trim() || undefined,
                  objetivosProximaSesion: objetivosProx.trim() || undefined,
                }
              : undefined,
          };
          completeSession(
            { patientId, episodeId, planId, sessionId: session.id, payload: soapPayload },
            { onSuccess: onClose, onError: onClose },
          );
        },
      },
    );
  };

  // ── Colors ────────────────────────────────────────────────────────────────
  const sectionBg       = useColorModeValue('gray.50',   'navy.750');
  const borderClr       = useColorModeValue('gray.200',  'gray.600');
  const labelColor      = useColorModeValue('gray.600',  'gray.400');
  const readOnlyBg      = useColorModeValue('gray.100',  'gray.700');
  const summaryBg       = useColorModeValue('brand.50',  'rgba(99,102,241,0.10)');
  const summaryBdr      = useColorModeValue('brand.200', 'brand.700');
  const modeInactiveClr = useColorModeValue('gray.400',  'gray.500');
  const soapSectionBg   = useColorModeValue('gray.50',   'navy.750');

  const fieldSm = { fontSize: 'sm' as const, borderRadius: '8px', size: 'sm' as const };

  // ── Step 1 ────────────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <VStack spacing="14px" align="stretch">

      {/* Service selector */}
      <FormControl isRequired isInvalid={!!step1Errors.serviceItemId}>
        <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
          Servicio prestado
        </FormLabel>
        {loadingItems ? (
          <HStack spacing="8px">
            <Spinner size="sm" color="brand.500" />
            <Text fontSize="sm" color={labelColor}>Cargando catálogo…</Text>
          </HStack>
        ) : (
          <Select
            placeholder="Seleccione un servicio del catálogo…"
            value={serviceItemId}
            onChange={e => { setServiceItemId(e.target.value); setStep1Errors(p => ({ ...p, serviceItemId: '' })); }}
            {...fieldSm}
          >
            {activeItems.map(item => (
              <option key={item.id} value={item.id}>{item.code} — {item.name}</option>
            ))}
          </Select>
        )}
        <FormErrorMessage fontSize="xs">{step1Errors.serviceItemId}</FormErrorMessage>
      </FormControl>

      {/* Base price */}
      <FormControl>
        <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
          Precio base (catálogo)
        </FormLabel>
        <InputGroup size="sm">
          <InputLeftAddon borderLeftRadius="8px" fontSize="sm">$</InputLeftAddon>
          <Input
            value={selectedItem ? Number(selectedItem.basePrice).toFixed(2) : '—'}
            isReadOnly
            bg={readOnlyBg}
            borderRightRadius="8px"
            cursor="default"
            _focus={{ boxShadow: 'none', borderColor: 'inherit' }}
            fontSize="sm"
            fontWeight="600"
          />
        </InputGroup>
      </FormControl>

      {/* Discount section */}
      <Box bg={sectionBg} border="1px solid" borderColor={borderClr} borderRadius="10px" p="14px">
        <Text fontSize="xs" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.06em" mb="10px">
          Descuento (opcional)
        </Text>
        <VStack spacing="10px" align="stretch">
          <HStack spacing="6px">
            <Button
              size="xs" borderRadius="6px"
              variant={discountMode === 'amount' ? 'solid' : 'outline'}
              colorScheme={discountMode === 'amount' ? 'brand' : 'gray'}
              color={discountMode !== 'amount' ? modeInactiveClr : undefined}
              onClick={() => { setDiscountMode('amount'); setDiscountValue(''); setStep1Errors(p => ({ ...p, discountValue: '' })); }}
            >
              Monto fijo ($)
            </Button>
            <Button
              size="xs" borderRadius="6px"
              variant={discountMode === 'percent' ? 'solid' : 'outline'}
              colorScheme={discountMode === 'percent' ? 'brand' : 'gray'}
              color={discountMode !== 'percent' ? modeInactiveClr : undefined}
              onClick={() => { setDiscountMode('percent'); setDiscountValue(''); setStep1Errors(p => ({ ...p, discountValue: '' })); }}
            >
              Porcentaje (%)
            </Button>
          </HStack>

          <FormControl isInvalid={!!step1Errors.discountValue}>
            <FormLabel fontSize="xs" color={labelColor} mb="4px">
              {discountMode === 'amount' ? 'Monto del descuento' : 'Porcentaje de descuento'}
            </FormLabel>
            <InputGroup size="sm">
              <InputLeftAddon borderLeftRadius="8px" fontSize="sm">
                {discountMode === 'amount' ? '$' : '%'}
              </InputLeftAddon>
              <Input
                type="number" min={0} max={discountMode === 'percent' ? 100 : undefined} step="0.01"
                placeholder="0"
                value={discountValue}
                onChange={e => { setDiscountValue(e.target.value); setStep1Errors(p => ({ ...p, discountValue: '', discountReason: '' })); }}
                isDisabled={!selectedItem}
                borderRightRadius="8px" fontSize="sm"
              />
            </InputGroup>
            <FormErrorMessage fontSize="xs">{step1Errors.discountValue}</FormErrorMessage>
          </FormControl>

          {hasDiscount && (
            <FormControl isRequired isInvalid={!!step1Errors.discountReason}>
              <FormLabel fontSize="xs" color={labelColor} mb="4px">
                Motivo del descuento <Text as="span" color="red.400" fontSize="xs">*</Text>
              </FormLabel>
              <Textarea
                placeholder="Ej: Convenio corporativo ABC, seguro médico…"
                value={discountReason}
                onChange={e => { setDiscountReason(e.target.value); setStep1Errors(p => ({ ...p, discountReason: '' })); }}
                rows={2} resize="none" maxLength={500} fontSize="sm" borderRadius="8px" size="sm"
              />
              <FormErrorMessage fontSize="xs">{step1Errors.discountReason}</FormErrorMessage>
            </FormControl>
          )}
        </VStack>
      </Box>

      {/* Summary */}
      {selectedItem && (
        <Box bg={summaryBg} border="1px solid" borderColor={summaryBdr} borderRadius="10px" p="14px">
          <Text fontSize="xs" fontWeight="700" color="brand.600" textTransform="uppercase" letterSpacing="0.06em" mb="10px">
            Resumen del cobro
          </Text>
          <VStack spacing="6px" align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color={labelColor}>Precio base</Text>
              <Text fontSize="sm" fontWeight="600">${basePrice.toFixed(2)}</Text>
            </HStack>
            {hasDiscount && (
              <HStack justify="space-between">
                <Text fontSize="sm" color="red.500">
                  Descuento{discountMode === 'percent' ? ` (${discountNum}%)` : ''}
                </Text>
                <Text fontSize="sm" fontWeight="600" color="red.500">- ${discountAmt.toFixed(2)}</Text>
              </HStack>
            )}
            <Divider />
            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="700">Total a cobrar</Text>
              <Text fontSize="lg" fontWeight="800" color="brand.600">${totalAmount.toFixed(2)}</Text>
            </HStack>
          </VStack>
        </Box>
      )}
    </VStack>
  );

  // ── Step 2 ────────────────────────────────────────────────────────────────
  const renderStep2 = () => {
    return (
      <VStack spacing="14px" align="stretch">

        {/* Charge summary chip */}
        <HStack
          bg={summaryBg} border="1px solid" borderColor={summaryBdr}
          borderRadius="10px" px="14px" py="10px" spacing="10px"
        >
          <Icon as={MdAttachMoney} color="brand.500" w="18px" h="18px" />
          <Box flex="1">
            <Text fontSize="xs" color={labelColor}>Cobro a registrar</Text>
            <HStack spacing="8px" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="700">{selectedItem?.name}</Text>
              <Badge colorScheme="brand" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
                ${totalAmount.toFixed(2)}
              </Badge>
              {hasDiscount && (
                <Badge colorScheme="red" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
                  Descuento aplicado
                </Badge>
              )}
            </HStack>
          </Box>
        </HStack>

        {/* SOAP form */}
        <Box bg={soapSectionBg} border="1px solid" borderColor={borderClr} borderRadius="10px" p="14px">
          <HStack spacing="8px" mb="12px">
            <Badge colorScheme="blue" borderRadius="full" px="10px" py="2px" fontSize="xs" fontWeight="700">
              S — Subjetivo
            </Badge>
          </HStack>
          <VStack spacing="10px" align="stretch">
            <FormControl isRequired isInvalid={motivoError}>
              <FormLabel fontSize="xs" color={labelColor} mb="4px">Motivo / evolución de sesión</FormLabel>
              <Textarea
                value={motivoSesion}
                onChange={e => { setMotivoSesion(e.target.value); setMotivoError(false); }}
                placeholder="Describe la evolución del paciente en esta sesión…"
                rows={3} resize="none" fontSize="sm" borderRadius="8px" size="sm"
              />
              <FormErrorMessage fontSize="xs">Mínimo 5 caracteres.</FormErrorMessage>
            </FormControl>
            <HStack spacing="10px" align="flex-start">
              <FormControl maxW="110px">
                <FormLabel fontSize="xs" color={labelColor} mb="4px">EVA Dolor (0–10)</FormLabel>
                <Input
                  type="number" min={0} max={10}
                  value={evaDolor}
                  onChange={e => setEvaDolor(e.target.value)}
                  placeholder="—"
                  fontSize="sm" borderRadius="8px" size="sm"
                />
              </FormControl>
              <FormControl flex="1">
                <FormLabel fontSize="xs" color={labelColor} mb="4px">Síntomas referidos</FormLabel>
                <Input
                  value={sintomasReferidos}
                  onChange={e => setSintomasReferidos(e.target.value)}
                  placeholder="Hormigueo, irradiación…"
                  fontSize="sm" borderRadius="8px" size="sm"
                />
              </FormControl>
            </HStack>
          </VStack>
        </Box>

        <Box bg={soapSectionBg} border="1px solid" borderColor={borderClr} borderRadius="10px" p="14px">
          <HStack spacing="8px" mb="12px">
            <Badge colorScheme="green" borderRadius="full" px="10px" py="2px" fontSize="xs" fontWeight="700">
              O — Objetivo
            </Badge>
          </HStack>
          <FormControl>
            <FormLabel fontSize="xs" color={labelColor} mb="4px">Hallazgos examen físico</FormLabel>
            <Textarea
              value={hallazgos}
              onChange={e => setHallazgos(e.target.value)}
              placeholder="Limitación ROM, signos clínicos…"
              rows={2} resize="none" fontSize="sm" borderRadius="8px" size="sm"
            />
          </FormControl>
        </Box>

        <Box bg={soapSectionBg} border="1px solid" borderColor={borderClr} borderRadius="10px" p="14px">
          <HStack spacing="8px" mb="12px">
            <Badge colorScheme="purple" borderRadius="full" px="10px" py="2px" fontSize="xs" fontWeight="700">
              P — Plan
            </Badge>
          </HStack>
          <VStack spacing="10px" align="stretch">
            <FormControl>
              <FormLabel fontSize="xs" color={labelColor} mb="4px">Técnicas aplicadas</FormLabel>
              <Input
                value={tecnicas}
                onChange={e => setTecnicas(e.target.value)}
                placeholder="Tracción lumbar, TENS…"
                fontSize="sm" borderRadius="8px" size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={labelColor} mb="4px">Objetivos próxima sesión</FormLabel>
              <Input
                value={objetivosProx}
                onChange={e => setObjetivosProx(e.target.value)}
                placeholder="Alcanzar flexión 75°…"
                fontSize="sm" borderRadius="8px" size="sm"
              />
            </FormControl>
          </VStack>
        </Box>

      </VStack>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="7px">

        <ModalHeader fontSize="md" fontWeight="700" pb="2px">
          Registrar Atención — Sesión #{session.numeroSesion}
          <Text fontSize="xs" fontWeight="400" color={labelColor} mt="2px">
            {new Date(session.fechaSesion).toLocaleDateString('es-EC', {
              day: '2-digit', month: 'long', year: 'numeric',
            })}
          </Text>
          <Box mt="12px">
            <Steps steps={MODAL_STEPS} activeStep={step - 1} size="sm" />
          </Box>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {step === 1 ? renderStep1() : renderStep2()}
        </ModalBody>

        <ModalFooter gap="8px">
          {step === 1 ? (
            <>
              <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isPending}>
                Cancelar
              </Button>
              <Button
                colorScheme="brand"
                size="sm"
                borderRadius="8px"
                rightIcon={<Icon as={MdChevronRight} />}
                onClick={handleContinue}
                isDisabled={!selectedItem}
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} isDisabled={isPending}>
                ← Volver
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                size="sm"
                borderRadius="8px"
                rightIcon={<Icon as={MdSkipNext} />}
                onClick={handleSkipSoap}
                isLoading={isPendingCharge && !isPendingComplete}
                loadingText="Procesando…"
              >
                Omitir SOAP
              </Button>
              <Button
                colorScheme="green"
                size="sm"
                borderRadius="8px"
                onClick={handleSaveWithSoap}
                isLoading={isPending}
                loadingText="Guardando…"
              >
                Guardar con SOAP
              </Button>
            </>
          )}
        </ModalFooter>

      </ModalContent>
    </Modal>
  );
}
